import React, { useState } from "react";
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { alpha } from "@mui/material/styles";
import reportTemplateServices from "app/services/automations/report-template-services";
import generatedFileServices, { requestErrors, waitUntilReady } from "app/services/automations/generated-file-services";
import ReportDateRange, { todayIso } from "../ReportDateRange";
import ReportViewerDialog from "../ReportViewerDialog";
import { getReportType } from "../types";

// Una plantilla del listado: se eligen las fechas (y el local, si no lo fija) y desde aquí mismo se ve el reporte en
// pantalla completa (en HTML, sin generar el PDF) o se descarga el PDF. Con las mismas fechas, volver a verlo o a
// descargarlo reutiliza lo ya armado.
const ReportTemplateRow = ({ template, stores, onOpenMenu }) => {
    const suggested = template.suggested_period || { from: todayIso(), to: todayIso() };
    const [period, setPeriod] = useState(suggested);
    const [storeId, setStoreId] = useState(template.store?.id || "");
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [generated, setGenerated] = useState(null);
    const [viewer, setViewer] = useState({ open: false, key: null, data: null, loading: false, error: null });
    const definition = getReportType(template.report_type);
    const Icon = definition?.icon || AssessmentOutlinedIcon;
    const needsStore = !template.store;
    const key = `${storeId}|${period.from}|${period.to}`;

    const ensureFile = async () => {
        if (generated?.key === key) return generated.file;
        const requested = await reportTemplateServices.generate(template.id, { ...period, store_id: needsStore ? storeId : undefined });
        const file = await waitUntilReady(requested.id);
        setGenerated({ key, file });
        return file;
    };

    const failure = (requestError) => (requestError?.response ? requestErrors(requestError, "No se pudo generar el PDF.") : requestError.message);

    const view = async () => {
        setError(null);
        if (viewer.key === key && viewer.data) {
            setViewer((current) => ({ ...current, open: true }));
            return;
        }
        setViewer({ open: true, key, data: null, loading: true, error: null });
        try {
            const data = await reportTemplateServices.preview(template.id, { ...period, store_id: needsStore ? storeId : undefined });
            setViewer((current) => (current.key === key ? { ...current, data, loading: false } : current));
        } catch (requestError) {
            const message = requestErrors(requestError, "No se pudo armar el reporte.");
            setViewer((current) => (current.key === key ? { ...current, loading: false, error: message } : current));
        }
    };

    const download = async () => {
        setBusy("download");
        setError(null);
        try {
            await generatedFileServices.download(await ensureFile());
        } catch (requestError) {
            setError(failure(requestError));
        } finally {
            setBusy(null);
        }
    };

    const missingStore = needsStore && !storeId;
    const storeName = template.store?.name || stores.find((store) => `${store.id}` === `${storeId}`)?.name;

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), display: "grid", placeItems: "center", flexShrink: 0 }}>
                    <Icon color="primary" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body1" fontWeight={600}>{template.name}</Typography>
                        <Chip size="small" variant="outlined" label={template.shared ? "Compartida" : "Personal"} color={template.shared ? "primary" : "default"} />
                        {!template.mine && <Typography variant="caption" color="text.secondary">de {template.owner.name}</Typography>}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{template.summary}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {template.store ? `Local: ${template.store.name}` : "El local se elige al generar"}
                        {template.default_period !== "none" ? ` · Período sugerido: ${template.default_period_label}` : ""}
                    </Typography>
                </Box>
                <Tooltip title="Acciones">
                    <IconButton size="small" onClick={(event) => onOpenMenu(event.currentTarget, template)}><MoreVertIcon fontSize="small" /></IconButton>
                </Tooltip>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ mt: 1.5 }}>
                {needsStore && (
                    <Autocomplete
                        size="small" options={stores} sx={{ width: { xs: "100%", md: 240 } }}
                        value={stores.find((store) => `${store.id}` === `${storeId}`) || null}
                        getOptionLabel={(option) => option?.name || ""}
                        onChange={(event, value) => setStoreId(value?.id || "")}
                        renderInput={(inputParams) => <TextField {...inputParams} label="Local" />}
                    />
                )}
                <Box sx={{ width: { xs: "100%", md: 340 } }}>
                    <ReportDateRange size="small" from={period.from} to={period.to} disabled={!!busy}
                                     onChange={(patch) => setPeriod((current) => ({ ...current, ...patch }))} />
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" disabled={missingStore} onClick={view} startIcon={<VisibilityOutlinedIcon />}>
                        Ver
                    </Button>
                    <Button variant="contained" disabled={!!busy || missingStore} onClick={download}
                            startIcon={busy === "download" ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}>
                        Descargar
                    </Button>
                </Stack>
            </Stack>
            {!!busy && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>Generando el PDF… también quedará en Descargas.</Typography>}
            {!!error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

            <ReportViewerDialog
                open={viewer.open} onClose={() => setViewer((current) => ({ ...current, open: false }))}
                title={template.name}
                subtitle={[storeName, viewer.data?.period?.label, template.summary].filter(Boolean).join(" · ")}
                Preview={definition?.Preview} data={viewer.data} loading={viewer.loading} error={viewer.error}
                onDownload={download} downloading={busy === "download"} downloadError={viewer.open ? error : null}
            />
        </Paper>
    );
};

export default ReportTemplateRow;
